'use client'

import * as Flags from 'country-flag-icons/react/3x2'
import { useLocale, useTranslations } from 'next-intl'
import { ReactNode, useMemo } from 'react'

import { ModelsAccountStatus } from '@/api/generated-api'
import { URLS } from '@/configs/urls'
import { VerifiedMark } from '@/features/clubs/components/VerifiedMark'
import {
  athleteDisplayName,
  isAthleteProfile
} from '@/features/clubs/lib/athleteProfile'
import { resolveClubSportDisplay } from '@/features/clubs/lib/resolveClubSportDisplay'
import type { ModelsClubWithSport } from '@/features/clubs/types/clubSport'
import { useReferralLinkEnabled } from '@/features/referrals/hooks'
import { SportTypeIcon } from '@/features/sport-types/components/SportTypeIcon'
import { useSportTypes } from '@/features/sport-types/hooks'
import { useRouter } from '@/i18n/routing'
import { formatLocation } from '@/lib/localizeCountryName'
import { showToast } from '@/providers/ToastProvider/ToastMessage'
import Button from '@/shared/ui/Button'
import countries from '@/ui/countries.json'
import { CopyLinkIcon } from '@/ui/icons/CopyLinkIcon'
import { InModerationIcon } from '@/ui/icons/InModerationIcon'
import Image from '@/ui/MediaImage'
import { shouldUnoptimizeImageSrc } from '@/utils/imageUrl'

import ClubSocialLinks from '../ClubSocialLinks'
import ExpandableText from '../ExpandableText'
import S from './ClubProfileCard.module.scss'

const DEFAULT_AVATAR = '/assets/default-avatar.svg'

interface ClubProfileCardProps {
  club: ModelsClubWithSport
  isOwner: boolean
  actionSlot?: ReactNode
}

type EditButtonMode = 'active' | 'disabled' | 'hidden'

type FlagComponent = React.ComponentType<
  React.SVGProps<SVGSVGElement> & { title?: string }
>
const FLAG_COMPONENTS = Flags as unknown as Record<string, FlagComponent>

const CountryFlag = ({
  countryCode,
  className
}: {
  countryCode: string
  className?: string
}) => {
  const Component = FLAG_COMPONENTS[countryCode]
  if (!Component) return null
  return <Component className={className} title={countryCode} />
}

const resolveEditButtonMode = (
  status: ModelsClubWithSport['status']
): EditButtonMode => {
  if (status === ModelsAccountStatus.AccountStatusInEdit) return 'disabled'
  if (status === ModelsAccountStatus.AccountStatusActive) return 'active'
  return 'hidden'
}

const ClubProfileCard = ({
  club,
  isOwner,
  actionSlot
}: ClubProfileCardProps) => {
  const router = useRouter()
  const locale = useLocale()
  const t = useTranslations('club.card')
  const tBlocked = useTranslations('club.blockedProfile')
  const { data: sportTypes = [] } = useSportTypes()

  const { data: referralLinkData } = useReferralLinkEnabled(isOwner)

  const avatarSrc = club.club_image || DEFAULT_AVATAR
  // BAT-1106: an athlete is shown by its Display Name.
  const isAthlete = isAthleteProfile(club)
  const displayName = isAthlete
    ? athleteDisplayName(club)
    : club.club_name || '—'
  const sport = useMemo(
    () => resolveClubSportDisplay(club, sportTypes),
    [club, sportTypes]
  )
  // The backend stores country as an English name string. Map it to an ISO code
  // so we can both show the flag and localize the label (OCE: country/city must
  // render in the platform language, not only English).
  const countryCode = useMemo(() => {
    const name = (club.country || '').trim().toLowerCase()
    if (!name) return ''
    const match = (countries as Array<{ name: string; code: string }>).find(
      c => c.name.trim().toLowerCase() === name
    )
    return match?.code || ''
  }, [club.country])
  // OCE-635: country + city stored as separate fields, joined for display
  // ("Berlin, Germany"). Country is localized via Intl.DisplayNames when we have
  // an ISO code; falls back to the raw stored name otherwise.
  // BAT-835: with the region between («Springfield, Illinois, United States»).
  const locationText = formatLocation(
    club.city,
    club.country,
    locale,
    club.region
  )
  const hasLocation = Boolean(locationText)

  const isDeletedAccount =
    isOwner && club.status === ModelsAccountStatus.AccountStatusDeleted
  const isBlockedAccount =
    isOwner && club.status === ModelsAccountStatus.AccountStatusBlocked
  const hasAccountNotice = isDeletedAccount || isBlockedAccount
  const editButtonMode = resolveEditButtonMode(club.status)
  const isEditVisible = isOwner && editButtonMode !== 'hidden'
  const isUnderModeration =
    club.status === ModelsAccountStatus.AccountStatusInEdit
  const showModerationPill = isEditVisible && isUnderModeration

  const handleEdit = () => {
    router.push(`${URLS.CLUB_REGISTER}?edit=1`)
  }

  const referralCode = referralLinkData?.code?.trim() || null
  const computedReferralLink = referralCode
    ? `${(process.env.NEXT_PUBLIC_APP_URL || '').replace(
        /\/+$/,
        ''
      )}/r/${encodeURIComponent(referralCode)}`
    : null
  const referralLink = referralLinkData?.link?.trim() || computedReferralLink

  const handleCopyReferralLink = async () => {
    if (!referralLink) return
    await navigator.clipboard.writeText(referralLink)
    showToast({
      type: 'success',
      title: t('copiedReferralLinkSuccess')
    })
  }

  return (
    <div className={S.card}>
      <div className={S.cardInner}>
        {/* BAT-1138: logo | info | actions on a grid — the buttons take
            their own column instead of sitting absolutely over the text. */}
        <div className={S.mainContent}>
          <div className={S.avatarSection}>
            <div className={S.avatarWrapper}>
              <Image
                src={avatarSrc}
                alt={isAthlete ? t('alt.athlete') : t('alt.club')}
                width={270}
                height={270}
                className={S.avatar}
                unoptimized={shouldUnoptimizeImageSrc(club.club_image)}
              />
            </div>
          </div>

          <div className={S.infoSection}>
            <div className={S.topInfo}>
              <div className={hasAccountNotice ? S.nameHeaderRow : undefined}>
                <div className={S.nameRow}>
                  <h1 className={S.name}>{displayName}</h1>
                  {/* BAT-597: the Verified mark — a manual admin flag of
                      a club or an athlete, shown only when it is on. */}
                  {club.is_verified ? (
                    <VerifiedMark size={32} className={S.profileCheckMark} />
                  ) : null}
                </div>
                {isDeletedAccount ? (
                  <p className={S.deletedAccountNotice} role="status">
                    {t('deletedAccountNotice')}
                  </p>
                ) : null}
                {isBlockedAccount ? (
                  <p className={S.deletedAccountNotice} role="status">
                    {tBlocked('subtitle')}
                  </p>
                ) : null}
              </div>
              {(hasLocation || sport) && (
                <div className={S.bottomInfo}>
                  {hasLocation ? (
                    <div className={S.location}>
                      {countryCode ? (
                        <CountryFlag
                          countryCode={countryCode}
                          className={S.flagImage}
                        />
                      ) : null}
                      <span className={S.country}>{locationText}</span>
                    </div>
                  ) : null}
                  {sport ? (
                    <div className={S.sportRow}>
                      <SportTypeIcon iconUrl={sport.iconUrl} alt={sport.name} />
                      <span className={S.sportText}>{sport.name}</span>
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {club.description ? (
              <ExpandableText
                text={club.description}
                className={S.bio}
                lines={3}
                moreLabel={t('descriptionMore')}
                lessLabel={t('descriptionLess')}
              />
            ) : null}

            <ClubSocialLinks club={club} />
          </div>

          {isEditVisible && (
            <div className={S.actionButtons}>
              <div className={S.editRow}>
                <Button
                  className={S.actionButton}
                  color="secondary"
                  fullWidth
                  onClick={handleEdit}
                  disabled={editButtonMode === 'disabled'}
                  title={
                    isUnderModeration ? t('editDisabledTooltip') : undefined
                  }
                >
                  {t('editButton')}
                </Button>
                {showModerationPill && (
                  <div className={S.moderationPill}>
                    <InModerationIcon className={S.moderationIcon} />
                    <span className={S.moderationText}>
                      {t('moderationLabel')}
                    </span>
                  </div>
                )}
              </div>
              <Button
                className={S.actionButton}
                fullWidth
                onClick={handleCopyReferralLink}
                disabled={!referralLink}
                title={!referralLink ? '—' : undefined}
                type="button"
              >
                {t('referralLink')} <CopyLinkIcon className={S.copyIcon} />
              </Button>
            </div>
          )}

          {actionSlot ? <div className={S.actionSlot}>{actionSlot}</div> : null}
        </div>
      </div>
    </div>
  )
}

export default ClubProfileCard
